import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pill, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { DataTable, type Column } from "@/components/DataTable";
import { DetailDrawer, type DrawerField } from "@/components/DetailDrawer";

export const Route = createFileRoute("/admin/administrateur/pharma/stocks/")({
  component: Page,
});

interface StockRow {
  id: number;
  dci: string;
  brand: string | null;
  form: string | null;
  dosage: string | null;
  unit_price: number | null;
  quantity: number;
  threshold: number;
  expiry_date: string | null;
  controlled: boolean;
}

function Page() {
  const [data, setData] = useState<StockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ low_stock: number; controlled_drugs: number; total_drugs: number } | null>(null);
  const [filter, setFilter] = useState<"all" | "low">("all");
  const [drawer, setDrawer] = useState<StockRow | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.pharmacyStock({ lowOnly: filter === "low" }), api.pharmacyStats()]);
      setData((r as any).items);
      setStats(s as any);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [filter]);

  const columns: Column<StockRow>[] = [
    { key: "id", label: "#", width: "60px" },
    { key: "dci", label: "DCI", sortable: true },
    { key: "brand", label: "Marque", render: (r) => r.brand || "—" },
    { key: "dosage", label: "Dosage", render: (r) => <span className="text-xs">{r.dosage} · {r.form}</span> },
    { key: "quantity", label: "Quantité", sortable: true, render: (r) => (
      <span className={r.quantity < r.threshold ? "text-red-400 font-bold" : ""}>
        {r.quantity} <span className="text-xs text-muted-foreground">/ seuil {r.threshold}</span>
      </span>
    )},
    { key: "unit_price", label: "PU", render: (r) => r.unit_price ? <span className="font-mono">{r.unit_price} MAD</span> : "—" },
    { key: "expiry_date", label: "Péremption", sortable: true, render: (r) => {
      if (!r.expiry_date) return "—";
      const d = new Date(r.expiry_date);
      const days = Math.floor((d.getTime() - Date.now()) / 86_400_000);
      return <span className={days < 90 ? "text-red-400" : days < 180 ? "text-amber-400" : ""}>{r.expiry_date} <span className="text-xs text-muted-foreground">({days}j)</span></span>;
    }},
    { key: "controlled", label: "Type", tone: (r) => r.controlled ? "destructive" : "outline", render: (r) => r.controlled ? "Stupéfiant" : "Standard" },
  ];

  const totalValue = data.reduce((acc, r) => acc + (r.unit_price || 0) * r.quantity, 0);
  const lowCount = data.filter((r) => r.quantity < r.threshold).length;

  return (
    <div className="space-y-4 p-6">
      <Link to="/admin/administrateur" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Dashboard Administrateur
      </Link>
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Pill className="h-6 w-6" /> Stocks pharmacie
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Stock actuel · Seuils · Péremption · Stupéfiants</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">DCI en stock</div><div className="mt-1 text-2xl font-bold">{stats.total_drugs}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Alertes seuil</div><div className="mt-1 text-2xl font-bold text-amber-400">{stats.low_stock}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Stupéfiants</div><div className="mt-1 text-2xl font-bold text-red-400">{stats.controlled_drugs}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Valeur stock</div><div className="mt-1 text-2xl font-bold text-emerald-400">{Math.round(totalValue).toLocaleString()} MAD</div></CardContent></Card>
        </div>
      )}

      <DataTable
        title="Stock"
        subtitle={`${data.length} références affichées`}
        data={data}
        columns={columns}
        loading={loading}
        searchFields={["dci", "brand", "dosage", "form"]}
        onRowClick={(r) => setDrawer(r as StockRow)}
        actions={
          <div className="flex gap-2">
            <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")}>Tous</Button>
            <Button variant={filter === "low" ? "default" : "outline"} size="sm" onClick={() => setFilter("low")}>Sous seuil ({lowCount})</Button>
          </div>
        }
      />

      <DetailDrawer
        open={!!drawer}
        onOpenChange={(o) => !o && setDrawer(null)}
        title={drawer?.dci || ""}
        subtitle={drawer?.brand || ""}
        badge={drawer ? { label: drawer.controlled ? "STUPÉFIANT" : "Standard", tone: drawer.controlled ? "destructive" : "outline" } : undefined}
        fields={drawer ? [
          { label: "ID", value: String(drawer.id), mono: true },
          { label: "DCI", value: drawer.dci },
          { label: "Marque", value: drawer.brand || "—" },
          { label: "Forme", value: drawer.form || "—" },
          { label: "Dosage", value: drawer.dosage || "—" },
          { label: "Quantité", value: drawer.quantity, mono: true },
          { label: "Seuil alerte", value: drawer.threshold, mono: true },
          { label: "Prix unitaire", value: drawer.unit_price ? `${drawer.unit_price} MAD` : "—" },
          { label: "Péremption", value: drawer.expiry_date || "—" },
        ] : []}
      />
    </div>
  );
}